import { type CSSProperties, useCallback, useState } from 'react';
import { useViewer } from '../store/context';

const MIN_WIDTH = 180;
const MAX_WIDTH = 480;
const DEFAULT_WIDTH = 256; // matches the previous fixed md:w-64
const STEP = 16;
const STORAGE_KEY = 'markroom.sidebarWidth';

function clamp(value: number): number {
  return Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, value));
}

function readStoredWidth(): number {
  try {
    const stored = Number(localStorage.getItem(STORAGE_KEY));
    if (Number.isFinite(stored) && stored >= MIN_WIDTH && stored <= MAX_WIDTH) return stored;
  } catch {
    // ignore (storage unavailable)
  }
  return DEFAULT_WIDTH;
}

/**
 * Heading sidebar. On wide screens it's a static, drag-resizable column (so long
 * headings can show more text); on narrow (mobile) screens it becomes a slide-in
 * drawer with a backdrop, toggled from the header. Selecting an entry scrolls to
 * the heading and closes the drawer on mobile. The desktop width persists.
 */
export function TableOfContents() {
  const toc = useViewer((s) => s.toc);
  const sidebarOpen = useViewer((s) => s.sidebarOpen);
  const setSidebarOpen = useViewer((s) => s.setSidebarOpen);
  const status = useViewer((s) => s.status);
  const [width, setWidth] = useState(readStoredWidth);

  const persist = useCallback((value: number) => {
    try {
      localStorage.setItem(STORAGE_KEY, String(value));
    } catch {
      // ignore
    }
  }, []);

  // Drag the divider: track from the pointer-down position and write once on
  // release (not on every move).
  const onResizePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = width;
    let latest = startWidth;
    const onMove = (ev: PointerEvent) => {
      latest = clamp(startWidth + (ev.clientX - startX));
      setWidth(latest);
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      document.body.style.removeProperty('cursor');
      document.body.style.removeProperty('user-select');
      persist(latest);
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  const onResizeKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    let next: number;
    if (e.key === 'ArrowLeft') next = clamp(width - STEP);
    else if (e.key === 'ArrowRight') next = clamp(width + STEP);
    else if (e.key === 'Home') next = MIN_WIDTH;
    else if (e.key === 'End') next = MAX_WIDTH;
    else return;
    e.preventDefault();
    setWidth(next);
    persist(next);
  };

  const resetWidth = () => {
    setWidth(DEFAULT_WIDTH);
    persist(DEFAULT_WIDTH);
  };

  // No document, or a document with no headings: there's nothing to navigate, so
  // the rail (and its header toggle) stay hidden entirely.
  if (status !== 'ready' || toc.length === 0) return null;

  const onSelect = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    // Close the drawer on mobile after navigating.
    if (typeof matchMedia !== 'undefined' && matchMedia('(max-width: 767px)').matches) {
      setSidebarOpen(false);
    }
  };

  return (
    <>
      {sidebarOpen && (
        <button
          type="button"
          aria-label="Close contents"
          onClick={() => {
            setSidebarOpen(false);
          }}
          className="fixed inset-0 z-20 bg-black/30 md:hidden"
        />
      )}
      <nav
        aria-label="Table of contents"
        // The width var only takes effect at md+ (md:w-[var(--toc-w)]); the mobile
        // drawer keeps its fixed w-72.
        style={{ '--toc-w': `${String(width)}px` } as CSSProperties}
        className={`mr-toc ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } fixed inset-y-0 left-0 z-30 w-72 transform overflow-auto border-r border-neutral-200 bg-neutral-50 p-3 transition-transform md:static md:z-auto md:w-[var(--toc-w)] md:translate-x-0 dark:border-neutral-800 dark:bg-neutral-900 ${
          sidebarOpen ? 'md:block' : 'md:hidden'
        }`}
      >
        <ul className="space-y-0.5 text-sm">
          {toc.map((entry, i) => (
            <li key={`${entry.id}-${String(i)}`}>
              <button
                type="button"
                onClick={() => {
                  onSelect(entry.id);
                }}
                className="block w-full truncate rounded px-2 py-2 text-left text-neutral-600 hover:bg-neutral-200 hover:text-neutral-900 md:py-1 dark:text-neutral-400 dark:hover:bg-neutral-800 dark:hover:text-neutral-100"
                style={{ paddingLeft: `${String(0.5 + (entry.depth - 1) * 0.75)}rem` }}
                title={entry.text}
              >
                {entry.text}
              </button>
            </li>
          ))}
        </ul>
      </nav>
      {/* Drag handle: desktop only. Sits in the content-bg gutter just right of the
          rail's border; double-click resets, arrows nudge. */}
      {sidebarOpen && (
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize the contents sidebar"
          aria-valuenow={width}
          aria-valuemin={MIN_WIDTH}
          aria-valuemax={MAX_WIDTH}
          tabIndex={0}
          onPointerDown={onResizePointerDown}
          onKeyDown={onResizeKeyDown}
          onDoubleClick={resetWidth}
          className="hidden w-1.5 shrink-0 cursor-col-resize touch-none bg-transparent transition-colors hover:bg-blue-500/30 focus-visible:bg-blue-500/40 focus-visible:outline-none active:bg-blue-500/40 md:block"
        />
      )}
    </>
  );
}
